import type { TFunction } from "i18next";
import { DateTime, Info } from "luxon";
import { type AutoApproveRule, FULL_SHIFT_COVERAGE, type ShiftHourWindow } from "../../../../../utils/models";
import { DAYS_IN_WEEK, isWeekdayCode, WEEKDAY_CODES, WEEKDAY_PRESETS, WINDOW_DISPLAY_FORMAT, WINDOW_TIME_FORMAT } from "./autoApproveConsts";

const K = "department.config.staffApp.autoApprove.summary";

export const sameDays = (days: readonly string[], preset: readonly string[]): boolean =>
	days.length === preset.length && preset.every((day) => days.includes(day));

const daysOf = (t: TFunction, days: readonly string[], locale: string): string => {
	if (days.length === 0 || days.length === DAYS_IN_WEEK) return t(`${K}.every_day`);
	if (sameDays(days, WEEKDAY_PRESETS.weekdays)) return t(`${K}.weekdays`);
	if (sameDays(days, WEEKDAY_PRESETS.weekend)) return t(`${K}.weekends`);
	const names = Info.weekdays("short", { locale });
	return WEEKDAY_CODES.flatMap((code, index) => (days.includes(code) ? [names[index]] : [])).join(", ");
};

export const windowLabelOf = (window: ShiftHourWindow, locale: string): string => {
	const start = DateTime.fromFormat(window.start, WINDOW_TIME_FORMAT).setLocale(locale);
	const end = DateTime.fromFormat(window.end, WINDOW_TIME_FORMAT).setLocale(locale);
	return `${start.toFormat(WINDOW_DISPLAY_FORMAT)}–${end.toFormat(WINDOW_DISPLAY_FORMAT)}`;
};

/** The backend approves no one under a rule with no max overtime: that gate fails every pickup. */
export const approvesNoOne = (rule: AutoApproveRule): boolean => rule.maxOt == null;

/** "Weekends · short by 1+ · overtime up to 4h": every condition the rule sets, in the order a manager reads a shift. */
export const ruleSummaryOf = (t: TFunction, rule: AutoApproveRule, locale: string): string => {
	const days = rule.shiftDays.filter(isWeekdayCode);
	const parts = [daysOf(t, days, locale)];
	const windows = rule.shiftHours.map((window) => windowLabelOf(window, locale));
	if (windows.length > 0) parts.push(new Intl.ListFormat(locale, { style: "short", type: "disjunction" }).format(windows));
	if (rule.timeBeforeShift != null) parts.push(t(`${K}.within`, { hours: rule.timeBeforeShift }));
	if (rule.staffingShortage != null) parts.push(t(`${K}.short_by`, { count: rule.staffingShortage }));
	// DEFAULT: `jobIds` is optional on the rule; absent means every job, the same as an empty list.
	const jobCount = rule.jobIds?.length ?? 0;
	if (jobCount > 0) parts.push(t(`${K}.jobs`, { count: jobCount }));
	// Whole shift only is the starting point, so only a lower minimum is listed.
	if (rule.minShiftCoverage < FULL_SHIFT_COVERAGE) parts.push(t(`${K}.min_coverage`, { percent: rule.minShiftCoverage }));
	if (rule.maxOt === 0) parts.push(t(`${K}.no_overtime`));
	else if (rule.maxOt != null) parts.push(t(`${K}.overtime`, { hours: rule.maxOt }));
	return parts.join(" · ");
};
